export type AwaitedObject<T extends Record<string, unknown>> = {
  [K in keyof T]: Awaited<T[K]>;
};

/**
 * Accepts an object with awaitable values, and awaits all of them in parallel
 */
export const waitObj = async <O extends Record<string, unknown>>(
  awaitables: O,
): Promise<AwaitedObject<O>> => {
  const keys = Object.keys(awaitables);
  const results = await Promise.all(Object.values(awaitables));

  return Object.fromEntries(keys.map((k, i) => [k, results[i]])) as AwaitedObject<O>;
};

export const sleep = (time: number) => {
  const boundedTime = Math.max(0, time);

  return new Promise((resolve) => setTimeout(resolve, boundedTime));
};

/**
 * Bounds a promise that has no timeout of its own. The shared api client sets no axios timeout, so
 * a request that never settles (a dead connection during a mobile network transition) otherwise
 * hangs its consumer forever with no error and no retry — react-query in particular reuses the
 * in-flight promise, so neither refetch intervals nor `isFetched` ever advance.
 *
 * The timed-out request is only detached, not aborted (the generated client accepts no signal);
 * the rejection is a plain Error, so callers classify it as a transient failure.
 */
export const raceWithTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number,
  description = "Request",
): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`${description} timed out after ${timeoutMs}ms`)),
      timeoutMs,
    );
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};
